import { type FC, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  SwitchButton,
  TooltipText,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import {
  useNotification,
  NotificationTypes,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import { FullCompanyApiType } from "../../../../util/types";

const PartnerSettingsContainer = styled.div`
  margin-top: 20px;
`;

const PartnerSettingsHeader = styled.div`
  margin-bottom: 15px;
`;

const PartnerSettingsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const PartnerSettingsBlock = styled.div`
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
`;

const SwitchRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SwitchLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface Props {
  data: FullCompanyApiType | undefined;
  onRefresh: () => void;
  partnersErrors?: string[];
  onMarkAsPartnerClick?: () => void;
}

export const CompanyPartnerSettingsTab: FC<Props> = ({
  data,
  onRefresh,
  partnersErrors = [],
  onMarkAsPartnerClick,
}) => {
  const { showNotification } = useNotification();
  const [togglingField, setTogglingField] = useState<string | null>(null);

  if (!data) {
    return null;
  }

  const handlePartnerSwitchChange = async (
    field:
      | "isAutoBankAccountVerificationEnabledForConsumerMerchants"
      | "isAutoIdVerificationEnabledForConsumerMerchants",
    value: boolean
  ) => {
    if (!data?._id) return;
    setTogglingField(field);
    try {
      await api.updateCompany(data._id, { [field]: value });
      showNotification("Successfully updated", NotificationTypes.SUCCESS);
      onRefresh();
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "An error has occurred"),
        NotificationTypes.WARNING
      );
    } finally {
      setTogglingField(null);
    }
  };

  return (
    <PartnerSettingsContainer>
      <PartnerSettingsHeader>
        <Typography variant={TypographyVariant.HEADER3}>
          Partner settings
        </Typography>
      </PartnerSettingsHeader>
      <PartnerSettingsContent>
        <PartnerSettingsBlock>
          {!data?.canManageCompanies && onMarkAsPartnerClick && (
            <TooltipText
              id="mark-as-partner-tooltip"
              text={
                partnersErrors.length === 0
                  ? "Company is ready to be marked as a partner"
                  : partnersErrors.join(", ")
              }
            >
              <span>
                <Button
                  onClick={onMarkAsPartnerClick}
                  variant={ButtonVariant.OUTLINED}
                  disabled={partnersErrors.length > 0}
                >
                  Mark as partner
                </Button>
              </span>
            </TooltipText>
          )}
          {data?.canManageCompanies && (
            <>
              <SwitchRow>
                <SwitchLabel>
                  <Typography variant={TypographyVariant.BODY2}>
                    Auto bank account verification for consumer merchants
                  </Typography>
                  <TooltipText
                    id="partner-auto-bank-tooltip"
                    text="If enabled, all new CONSUMER merchants' bank accounts under this partner will be automatically verified, and bank account statements will not need to be uploaded."
                  >
                    <span style={{ cursor: "help" }}>ⓘ</span>
                  </TooltipText>
                </SwitchLabel>
                <SwitchButton
                  checked={
                    data?.isAutoBankAccountVerificationEnabledForConsumerMerchants ??
                    false
                  }
                  onChange={(checked) =>
                    handlePartnerSwitchChange(
                      "isAutoBankAccountVerificationEnabledForConsumerMerchants",
                      checked
                    )
                  }
                  disabled={togglingField !== null}
                />
              </SwitchRow>
              <SwitchRow>
                <SwitchLabel>
                  <Typography variant={TypographyVariant.BODY2}>
                    Auto ID verification for consumer merchants
                  </Typography>
                  <TooltipText
                    id="partner-auto-id-tooltip"
                    text="If enabled, all new CONSUMER merchants under this partner will have their identity automatically verified."
                  >
                    <span style={{ cursor: "help" }}>ⓘ</span>
                  </TooltipText>
                </SwitchLabel>
                <SwitchButton
                  checked={
                    data?.isAutoIdVerificationEnabledForConsumerMerchants ??
                    false
                  }
                  onChange={(checked) =>
                    handlePartnerSwitchChange(
                      "isAutoIdVerificationEnabledForConsumerMerchants",
                      checked
                    )
                  }
                  disabled={togglingField !== null}
                />
              </SwitchRow>
            </>
          )}
        </PartnerSettingsBlock>
      </PartnerSettingsContent>
    </PartnerSettingsContainer>
  );
};

export default CompanyPartnerSettingsTab;
